/*
 * Carbisdale - CoreView
 *
 * Rendering fuer die Core Console.
 * Baut Entries und Input in Screen-Zeilen um,
 * rendert mit Styles und verwaltet Viewport/Scrollbar.
 */

import { Style, STYLES, MINIMAP, PARAGRAPH_SPACING, LINE_HEIGHT_Q, TIMING, headingHeightQ, collapseSpacing } from '../config.js'
import { parseMarkdownBlocks } from '../MarkdownBlockParser.js'
import {
    findFormatSpans,
    parseToStyledChars, parsedToFormatRuns, parsedCharsToString,
    collectMarkerPositions, findUrlSpans, getUrlSpanAtOffset,
    charDisplayWidth
} from '../TextProcessor.js'
import { buildStyledChars } from '../RichTextOps.js'
import { computeStringLayout } from '../GlyphMetrics.js'
import { LinePositionMap, buildLinePositionMap, enrichFormatRuns } from '../LinePositionMap.js'
import { buildMonoShiftedPositions } from '../textGeometry.js'

/**
 * Eine Zeile auf dem Screen (vereinfacht gegenueber EditorView ScreenLine).
 */
export class ConsoleLine {
    /**
     * @param {object} params
     * @param {number} params.entryIndex - Index des Entry (-1 fuer Input/Blank)
     * @param {number} params.startOffset - Start im Text
     * @param {number} params.endOffset - Ende im Text
     * @param {string} params.lineType - 'SYSTEM'|'USER'|'AI'|'ERROR'|'INPUT'|'BLANK'
     * @param {boolean} params.isFirstLine - Erste Zeile des Eintrags (mit Prefix)
     * @param {string} params.text - Der Text dieser Zeile (fuer schnelles Rendering)
     * @param {string} params.prefix - Prefix dieser Zeile ('> ', '[SYS] ', etc.)
     */
    constructor({ entryIndex = -1, startOffset = 0, endOffset = 0,
        lineType = 'BLANK', isFirstLine = false, text = '', prefix = '' } = {}) {
        this.entryIndex = entryIndex
        this.startOffset = startOffset
        this.endOffset = endOffset
        this.lineType = lineType
        this.isFirstLine = isFirstLine
        this.text = text
        this.prefix = prefix
        this.pixelWidth = 0  // >0 = proportional gerendert
        // Block-Rendering (AI Markdown-Bloecke)
        this.blockType = null       // 'paragraph'|'heading'|'list'|'code'|'blockquote'
        this.blockId = -1           // Block-Index innerhalb des Entry
        this.blockText = null       // Display-Text des Blocks (null = entry.text verwenden)
        this.spacingBefore = 0      // Viertel-Zeilen-Abstand
        this.heightQ = 4            // Zeilenhoehe in Quarterlines (4 = Basis, 6 = Normal, 7-10 = Headings)
        this.blockLevel = 0         // Heading-Level aus MarkdownBlockParser (1-3, 0 = kein Heading)
        this.forceMonospace = false  // Code-Bloecke: Grid-Rendering erzwingen
    }
}

// Prefix-Laengen fuer Entry-Typen
const ENTRY_PREFIXES = {
    SYSTEM: '[SYS] ',
    USER: '> ',
    AI: '',
    ERROR: '[ERR] ',
    THINKING: '[...] ',
    REVIEW_PANEL: '[REV] '
}

// Einrueckung fuer Continuation-Zeilen (gleiche Breite wie Prefix)
const CONTINUATION_INDENTS = {
    SYSTEM: '      ',  // 6 Zeichen wie '[SYS] '
    USER: '  ',        // 2 Zeichen wie '> '
    AI: '',
    ERROR: '      ',   // 6 Zeichen wie '[ERR] '
    THINKING: '      ', // 6 Zeichen wie '[...] '
    REVIEW_PANEL: '      '  // 6 Zeichen wie '[REV] '
}

// Style fuer jeden Entry-Typ
const ENTRY_STYLES = {
    SYSTEM: Style.CONSOLE_SYSTEM,
    USER: Style.CONSOLE_USER,
    AI: Style.CONSOLE_AI,
    ERROR: Style.CONSOLE_ERROR,
    THINKING: Style.CONSOLE_THINKING,
    REVIEW_PANEL: Style.CONSOLE_REVIEW_PANEL
}

/**
 * CoreView — Rendering-Engine der Console.
 */
export class CoreView {
    /**
     * @param {CoreModel} model
     * @param {{x: number, y: number, width: number, height: number}} bounds
     */
    constructor(model, bounds) {
        this.model = model
        this.width = bounds.width
        this.height = bounds.height

        /** @type {ConsoleLine[]} Alle Zeilen (Entries + Blank + Input) */
        this.lines = []

        /** Viewport: Erste sichtbare Zeile (Zeilen werden von unten gezaehlt) */
        this.viewportStart = 0


        // Cache fuer geparste Entries (Key: entryIndex oder -1 fuer Input, Value: {text, parsed})
        this._parsedCache = new Map()

        // Renderer-Referenz (wird von main.js gesetzt fuer proportionales Rendering)
        this.renderer = null

        // Quarter-Line Spacing (nur Proportional-Mode)
        this._lineQuarterY = []        // Kumulative Viertel-Y-Position jeder Zeile
        this._totalQuarters = 0        // Gesamthoehe in Vierteln
        this._viewportPixelOffset = 0  // Pixel-Offset fuer Viertel-Scrolling (nur Proportional)

        // Monospace-Scroll-Akkumulator: grid-based, Pixel-Input zu Zeilen sammeln
        this._monoScrollAccumulator = 0

        // PositionMap-Cache fuer proportionale Zeichen-Positionen (lineIndex → LinePositionMap)
        this._positionMaps = new Map()

        // Cache: Index der ersten Input-Zeile (wird in rebuildLines gesetzt)
        this._firstInputLineIndex = -1

        // URL-Span-Cache (Key: cacheKey, Value: {text, spans})
        this._urlSpanCache = new Map()
    }

    // === Line-Building ===

    /**
     * Baut alle Zeilen neu auf (Entries + Leerzeile + Input).
     */
    rebuildLines() {
        this.lines = []
        // _parsedCache bleibt bestehen — text-basierter Vergleich reicht als Invalidierung
        // _urlSpanCache bei jedem Rebuild leeren (verhindert unbegrenztes Wachstum)
        this._urlSpanCache.clear()
        this._positionMaps.clear()
        const availableWidth = this.width - 2  // -1 Scrollbar, -1 Safety

        if (availableWidth <= 0) return

        // 1. Entries rendern
        const entries = this.model.getEntries()
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i]
            const entryLines = this._wrapEntry(entry, i, availableWidth)
            this.lines.push(...entryLines)
        }

        // 2. Leerzeile vor Input (nur wenn es Entries gibt)
        // Proportional: BLANK wird durch spacingBefore ersetzt
        if (entries.length > 0 && !this._useQuarterSpacing()) {
            this.lines.push(new ConsoleLine({ lineType: 'BLANK' }))
        }

        // 3. Input-Zeilen
        this._firstInputLineIndex = this.lines.length
        const inputLines = this._wrapInput(availableWidth)
        this.lines.push(...inputLines)

        // 4. Spacing berechnen (Quarter-Line Abstands-System)
        this._computeSpacing()
    }

    /**
     * Bricht einen Entry in Zeilen um.
     * @param {ConsoleEntry} entry
     * @param {number} index - Entry-Index
     * @param {number} width - Verfuegbare Breite
     * @returns {ConsoleLine[]}
     */
    _wrapEntry(entry, index, width) {
        // AI-Entries: Block-basiertes Markdown-Rendering
        if (entry.type === 'AI') {
            return this._wrapAIEntry(entry, index, width)
        }

        // Collapsed THINKING: nur '[...]' anzeigen
        if (entry.type === 'THINKING' && entry.collapsed) {
            const line = new ConsoleLine({
                entryIndex: index,
                startOffset: 0,
                endOffset: 0,
                lineType: 'THINKING',
                isFirstLine: true,
                text: '[...]',
                prefix: ''
            })
            // Placeholder verwendet nicht-kursiven Style (siehe _getLineStyle)
            line.isCollapsedPlaceholder = true
            return [line]
        }

        const prefix = ENTRY_PREFIXES[entry.type] || ''
        const indent = CONTINUATION_INDENTS[entry.type] || ''
        const text = entry.text

        // Erste Zeile: Prefix + Text
        const firstLineWidth = width - prefix.length
        if (firstLineWidth <= 0) return []

        // Marker-Positionen fuer USER/AI Entries (haben Format-Marker)
        let markers = null
        if (entry.type === 'USER' || entry.type === 'AI') {
            const spans = findFormatSpans(text)
            if (spans.length > 0) {
                markers = collectMarkerPositions(spans)
            }
        }

        // fullParsed: Bold-aware Format-Runs fuer proportionalen Word-Wrap
        let fullParsed = null
        if (markers && this.renderer && this.renderer.isProportional()) {
            fullParsed = this._getCachedParsed(index, text, entry.type)
        }

        // Text an \n splitten, dann jedes Segment umbrechen
        const segments = text.split('\n')
        const result = []
        let textOffset = 0

        for (let s = 0; s < segments.length; s++) {
            const segment = segments[s]
            const isFirstSegment = (s === 0)

            // Breite: Erste Zeile des ersten Segments hat Prefix, Rest hat Indent
            const linePrefix = (isFirstSegment && result.length === 0) ? prefix : indent
            const lineWidth = width - linePrefix.length
            if (lineWidth <= 0) continue

            // heightQ für Word-Wrap: SYSTEM/ERROR/REVIEW_PANEL → 4 (SYSTEM), USER/THINKING → entsprechend
            const wrapHeightQ = (entry.type === 'SYSTEM' || entry.type === 'ERROR' || entry.type === 'REVIEW_PANEL')
                ? LINE_HEIGHT_Q.SYSTEM
                : (entry.type === 'THINKING' ? LINE_HEIGHT_Q.THINKING : LINE_HEIGHT_Q.NORMAL)
            const wrapped = this._wrapText(segment, lineWidth, markers, textOffset, fullParsed, wrapHeightQ)
            for (let w = 0; w < wrapped.length; w++) {
                const isFirst = (result.length === 0)
                const currentPrefix = isFirst ? prefix : indent
                const line = new ConsoleLine({
                    entryIndex: index,
                    startOffset: textOffset + wrapped[w].start,
                    endOffset: textOffset + wrapped[w].end,
                    lineType: entry.type,
                    isFirstLine: isFirst,
                    text: wrapped[w].text,
                    prefix: currentPrefix
                })
                if (wrapped[w].pixelWidth !== undefined) {
                    line.pixelWidth = wrapped[w].pixelWidth
                }
                result.push(line)
            }

            // +1 fuer das \n Zeichen (ausser beim letzten Segment)
            textOffset += segment.length + (s < segments.length - 1 ? 1 : 0)
        }

        // Leerer Entry → mindestens eine Zeile
        if (result.length === 0) {
            result.push(new ConsoleLine({
                entryIndex: index,
                startOffset: 0,
                endOffset: 0,
                lineType: entry.type,
                isFirstLine: true,
                text: '',
                prefix: prefix
            }))
        }

        return result
    }

    /**
     * Bricht die Input-Zeile in Zeilen um.
     * @param {number} width - Verfuegbare Breite
     * @returns {ConsoleLine[]}
     */
    _wrapInput(width) {
        const prompt = this.model.isLocked() ? '... ' : '> '
        const indent = this.model.isLocked() ? '    ' : '  '
        const text = this.model.getInputText()
        const inputWidth = width - prompt.length
        if (inputWidth <= 0) return []

        // Input nutzt RT-Spans (wie Editor), keine Markdown-Marker
        const markers = null

        // fullParsed: RT-basiert (konsistent mit Rendering und PositionMap)
        let fullParsed = null
        const formats = this.model.getInputFormats()
        if (this.renderer && this.renderer.isProportional()) {
            fullParsed = this._getCachedInputParsed(text, formats)
        }

        // Text an \n splitten
        const segments = text.split('\n')
        const result = []
        let textOffset = 0

        for (let s = 0; s < segments.length; s++) {
            const segment = segments[s]
            const lineWidth = (result.length === 0) ? inputWidth : (width - indent.length)
            if (lineWidth <= 0) continue

            const wrapped = this._wrapText(segment, lineWidth, markers, textOffset, fullParsed, LINE_HEIGHT_Q.NORMAL)

            // Leeres Segment → eine leere Zeile
            if (wrapped.length === 0) {
                const currentPrefix = (result.length === 0) ? prompt : indent
                result.push(new ConsoleLine({
                    entryIndex: -1,
                    startOffset: textOffset,
                    endOffset: textOffset,
                    lineType: 'INPUT',
                    isFirstLine: result.length === 0,
                    text: '',
                    prefix: currentPrefix
                }))
            }

            for (let w = 0; w < wrapped.length; w++) {
                const isFirst = (result.length === 0)
                const currentPrefix = isFirst ? prompt : indent
                const line = new ConsoleLine({
                    entryIndex: -1,
                    startOffset: textOffset + wrapped[w].start,
                    endOffset: textOffset + wrapped[w].end,
                    lineType: 'INPUT',
                    isFirstLine: isFirst,
                    text: wrapped[w].text,
                    prefix: currentPrefix
                })
                if (wrapped[w].pixelWidth !== undefined) {
                    line.pixelWidth = wrapped[w].pixelWidth
                }
                result.push(line)
            }

            textOffset += segment.length + (s < segments.length - 1 ? 1 : 0)
        }

        // Leerer Input → mindestens Prompt-Zeile
        if (result.length === 0) {
            result.push(new ConsoleLine({
                entryIndex: -1,
                startOffset: 0,
                endOffset: 0,
                lineType: 'INPUT',
                isFirstLine: true,
                text: '',
                prefix: prompt
            }))
        }

        return result
    }

    /**
     * Bricht Text in Zeilen um. Dispatcht auf proportionalen oder Mono-Modus.
     * @param {string} text
     * @param {number} width - Sichtbare Zeichenbreite (Mono) / wird fuer Pixel umgerechnet (Prop.)
     * @param {Set<number>|null} markerPositions - Unsichtbare Marker-Positionen (optional)
     * @param {number} baseOffset - Offset des Segments im Gesamttext (fuer Marker-Lookup)
     * @returns {Array<{start: number, end: number, text: string, pixelWidth?: number}>}
     */
    _wrapText(text, width, markerPositions = null, baseOffset = 0, fullParsed = null, heightQ = 0) {
        if (width <= 0) return []
        if (text.length === 0) return []

        // Proportional: pixel-basierter Word-Wrap
        if (this.renderer && this.renderer.isProportional()) {
            const availablePixelWidth = width * this.renderer.charWidth
            return this._wrapTextProportional(text, availablePixelWidth, markerPositions, baseOffset, fullParsed, heightQ)
        }

        // Mono: zeichenbasierter Word-Wrap
        return this._wrapTextMono(text, width, markerPositions, baseOffset)
    }

    // === Gemeinsame Word-Break-Hilfen fuer Mono und Proportional ===

    /** Sucht das letzte Space im Bereich (lineStart, lineEnd] — gibt -1 zurueck wenn keins. */
    _findWordBreakPos(text, lineStart, lineEnd) {
        for (let i = lineEnd; i > lineStart; i--) {
            if (text[i] === ' ') return i
        }
        return -1
    }

    /** Entfernt Trailing Spaces vor end (bis start). */
    _trimTrailingSpaces(text, end, start) {
        while (end > start && text[end - 1] === ' ') end--
        return end
    }

    /** Ueberspringt Spaces ab pos. */
    _skipSpaces(text, pos) {
        while (pos < text.length && text[pos] === ' ') pos++
        return pos
    }

    /**
     * Monospace Word-Wrap (bestehende Logik).
     */
    _wrapTextMono(text, width, markerPositions, baseOffset) {
        const hasMarkers = markerPositions && markerPositions.size > 0
        const result = []
        let pos = 0

        while (pos < text.length) {
            // Wie viele Data-Zeichen passen in 'width' sichtbare Zeichen?
            let dataEnd = pos
            let visibleCount = 0
            while (dataEnd < text.length && visibleCount < width) {
                // Codepoint-aware: Surrogate-Paare als ein Zeichen zaehlen
                const cp = text.codePointAt(dataEnd)
                const utf16Len = cp > 0xFFFF ? 2 : 1
                if (!hasMarkers || !markerPositions.has(baseOffset + dataEnd)) {
                    // Emoji belegt 2 Spalten (BMP + Supplementary)
                    const ch = text.substr(dataEnd, utf16Len)
                    const w = charDisplayWidth(ch)
                    if (visibleCount + w > width) break  // Passt nicht mehr rein
                    visibleCount += w
                }
                dataEnd += utf16Len
            }

            if (dataEnd >= text.length) {
                // Rest passt in eine Zeile
                result.push({ start: pos, end: text.length, text: text.substring(pos) })
                break
            }

            // Wortgrenze suchen (letztes Space innerhalb des Bereichs)
            const breakPos = this._findWordBreakPos(text, pos, dataEnd)

            if (breakPos <= pos) {
                // Kein Space gefunden → hart umbrechen bei dataEnd
                result.push({ start: pos, end: dataEnd, text: text.substring(pos, dataEnd) })
                pos = dataEnd
            } else {
                // Am Space umbrechen: Trailing Spaces am Zeilenende entfernen
                const textEnd = this._trimTrailingSpaces(text, breakPos, pos)
                result.push({ start: pos, end: textEnd, text: text.substring(pos, textEnd) })
                pos = this._skipSpaces(text, breakPos)
            }
        }

        return result
    }

    /**
     * Proportionaler Word-Wrap: Bricht Text pixel-basiert um.
     * @param {string} text - Segment-Text
     * @param {number} availablePixelWidth - Verfuegbare Breite in Pixel
     * @param {Set<number>|null} markerPositions - Unsichtbare Marker-Positionen
     * @param {number} baseOffset - Offset fuer Marker-Lookup
     * @returns {Array<{start: number, end: number, text: string, pixelWidth: number}>}
     */
    _wrapTextProportional(text, availablePixelWidth, markerPositions, baseOffset, fullParsed = null, heightQ = 0) {
        const metricsCache = heightQ > 0
            ? this.renderer.getGlyphMetricsCache(heightQ)
            : this.renderer.getGlyphMetricsCache()
        const scaleFactor = heightQ > 0
            ? this.renderer.getScaleFactor(heightQ) * heightQ / 4
            : this.renderer.getScaleFactor()
        if (!metricsCache) {
            // Fallback auf Mono
            const charWidth = Math.max(1, Math.round(availablePixelWidth / this.renderer.charWidth))
            return this._wrapTextMono(text, charWidth, markerPositions, baseOffset)
        }

        const hasMarkers = markerPositions && markerPositions.size > 0

        // Display-Text aufbauen (ohne Marker)
        let displayText, displayToData
        if (hasMarkers) {
            const chars = []
            const d2d = []
            for (let i = 0; i < text.length; ) {
                const cp = text.codePointAt(i)
                const ch = String.fromCodePoint(cp)
                if (!markerPositions.has(baseOffset + i)) {
                    chars.push(ch)
                    d2d.push(i)
                }
                i += ch.length
            }
            displayText = chars.join('')
            displayToData = d2d
        } else {
            displayText = text
            displayToData = null
        }

        if (displayText.length === 0) {
            return [{ start: 0, end: text.length, text, pixelWidth: 0 }]
        }

        // Format-Runs fuer den Display-Text (Bold-aware wenn fullParsed vorhanden)
        let formatRuns
        if (fullParsed) {
            // Segment-Chars aus fullParsed filtern (korrekte Bold-Erkennung auch cross-segment)
            const segmentChars = []
            for (const item of fullParsed) {
                if (item.dataOffset >= baseOffset && item.dataOffset < baseOffset + text.length) {
                    segmentChars.push(item)
                }
            }
            formatRuns = enrichFormatRuns(parsedToFormatRuns(segmentChars))
        } else {
            const parsed = parseToStyledChars(displayText, Style.CONSOLE_INPUT, { skipNewlines: true })
            formatRuns = enrichFormatRuns(parsedToFormatRuns(parsed))
        }

        // Layout bei Referenzgroesse berechnen
        const layout = computeStringLayout(displayText, metricsCache, metricsCache.baseStep, formatRuns)

        // Effective Positionen: beruecksichtigt Inline-Mono-Spans (z.B. CONSOLE_AI_CODE).
        // Mono-Glyphen werden im Renderer mit charWidth*heightQ/4 statt der proportionalen
        // Breite gesetzt — der Wrap muss das spiegeln, sonst stimmt die Zeilenbreite nicht
        // und Zeichen rutschen in die Scrollbar/Border.
        const cpLen = Array.from(displayText).length
        const monoCharWidth = this.renderer.charWidth * (heightQ > 0 ? heightQ : 4) / 4
        const effLeft = this._buildEffectivePositions(layout, formatRuns, scaleFactor, monoCharWidth, cpLen)

        const result = []
        let lineStart = 0  // Display-Index

        while (lineStart < displayText.length) {
            // Finde erstes Zeichen das ueber die Breite ragt
            let lineEnd = lineStart
            while (lineEnd < displayText.length) {
                const charEnd = effLeft[lineEnd + 1]
                const relEnd = charEnd - effLeft[lineStart]
                if (relEnd > availablePixelWidth && lineEnd > lineStart) break
                lineEnd++
            }

            if (lineEnd >= displayText.length) {
                // Rest passt in eine Zeile
                const dataStart = displayToData ? displayToData[lineStart] : lineStart
                const dataEnd = text.length
                const pw = effLeft[displayText.length] - effLeft[lineStart]
                result.push({ start: dataStart, end: dataEnd, text: text.substring(dataStart, dataEnd), pixelWidth: pw })
                break
            }

            // Wortgrenze suchen (letztes Space im Display-Text innerhalb des Bereichs)
            const breakDisplay = this._findWordBreakPos(displayText, lineStart, lineEnd)

            if (breakDisplay <= lineStart) {
                // Kein Space → hart umbrechen
                const dataStart = displayToData ? displayToData[lineStart] : lineStart
                const dataEnd = displayToData ? displayToData[lineEnd - 1] + 1 : lineEnd
                const pw = effLeft[lineEnd] - effLeft[lineStart]
                result.push({ start: dataStart, end: dataEnd, text: text.substring(dataStart, dataEnd), pixelWidth: pw })
                lineStart = lineEnd
            } else {
                // Am Space umbrechen: Trailing Spaces am Zeilenende entfernen
                const displayTextEnd = this._trimTrailingSpaces(displayText, breakDisplay, lineStart)
                const dataStart = displayToData ? displayToData[lineStart] : lineStart
                const dataEnd = displayToData ? displayToData[displayTextEnd - 1] + 1 : displayTextEnd
                const pw = effLeft[displayTextEnd] - effLeft[lineStart]
                result.push({ start: dataStart, end: dataEnd, text: text.substring(dataStart, dataEnd), pixelWidth: pw })
                lineStart = this._skipSpaces(displayText, breakDisplay)
            }
        }

        return result
    }

    /**
     * Wrapper um buildMonoShiftedPositions (textGeometry.js). Dünner Wrapper
     * bleibt aus Test-/API-Stabilitaet (CoreView.test.js ruft die Methode direkt),
     * die Logik selbst lebt im geteilten Helper.
     */
    _buildEffectivePositions(layout, formatRuns, scaleFactor, monoCharWidth, cpLen) {
        return buildMonoShiftedPositions(
            layout, formatRuns, STYLES, scaleFactor, monoCharWidth, cpLen
        )
    }

    // === AI Block-Rendering ===

    /**
     * Bricht einen AI-Entry in Bloecke auf und erzeugt ConsoleLines.
     * Nutzt parseMarkdownBlocks fuer Block-Level-Erkennung.
     * @param {ConsoleEntry} entry
     * @param {number} index - Entry-Index
     * @param {number} width - Verfuegbare Breite
     * @returns {ConsoleLine[]}
     */
    _wrapAIEntry(entry, index, width) {
        const blocks = parseMarkdownBlocks(entry.text)
        const result = []

        for (let b = 0; b < blocks.length; b++) {
            const block = blocks[b]
            const blockLines = this._wrapBlock(block, index, b, width)
            result.push(...blockLines)
        }

        // Leerer Entry → mindestens eine Zeile
        if (result.length === 0) {
            const line = new ConsoleLine({
                entryIndex: index, startOffset: 0, endOffset: 0,
                lineType: 'AI', isFirstLine: true, text: '', prefix: ''
            })
            line.blockType = 'paragraph'
            line.blockId = 0
            line.blockText = ''
            result.push(line)
        }

        return result
    }

    /**
     * Erzeugt ConsoleLines fuer einen einzelnen Markdown-Block.
     * @param {object} block - Block aus parseMarkdownBlocks
     * @param {number} entryIndex
     * @param {number} blockIndex
     * @param {number} width
     * @returns {ConsoleLine[]}
     */
    _wrapBlock(block, entryIndex, blockIndex, width) {
        switch (block.type) {
            case 'heading': {
                const lines = this._wrapBlockLines(block.text, entryIndex, blockIndex, width,
                    '', '', 'heading')
                // Heading-Level auf ConsoleLine speichern (für variable Zeilenhöhen)
                for (const l of lines) l.blockLevel = block.level || 1
                return lines
            }
            case 'list_item': {
                const indentStr = '  '.repeat(block.indent || 0)
                const prefix = indentStr + block.bullet + ' '
                const indent = ' '.repeat(prefix.length)
                return this._wrapBlockLines(block.text, entryIndex, blockIndex, width,
                    prefix, indent, 'list')
            }
            case 'hr': {
                // Horizontale Linie: Drittel der Breite, zentriert
                const hrLen = Math.max(3, Math.floor(width / 3))
                const hrText = '─'.repeat(hrLen)
                return this._wrapBlockLines(hrText, entryIndex, blockIndex, width,
                    '', '', 'hr')
            }
            case 'code':
                return this._wrapCodeBlock(block.text, entryIndex, blockIndex, width)
            case 'blockquote': {
                return this._wrapBlockLines(block.text, entryIndex, blockIndex, width,
                    '> ', '  ', 'blockquote')
            }
            default:
                return this._wrapBlockLines(block.text, entryIndex, blockIndex, width,
                    '', '', 'paragraph')
        }
    }

    /**
     * Generischer Block-Wrapper: Word-Wrap mit Prefix/Indent und Block-Metadaten.
     * @param {string} text - Display-Text des Blocks
     * @param {number} entryIndex
     * @param {number} blockIndex
     * @param {number} width
     * @param {string} prefix - Prefix fuer erste Zeile
     * @param {string} indent - Einrueckung fuer Continuation-Zeilen
     * @param {string} blockType
     * @returns {ConsoleLine[]}
     */
    _wrapBlockLines(text, entryIndex, blockIndex, width, prefix, indent, blockType) {
        if (!text && text !== '') return []

        const baseStyle = blockType === 'heading' ? Style.CONSOLE_AI_HEADING
            : blockType === 'blockquote' ? Style.CONSOLE_AI_BLOCKQUOTE
            : Style.CONSOLE_AI
        const cacheKey = `${entryIndex}_b${blockIndex}`

        // Marker-Positionen fuer Inline-Markdown
        const spans = findFormatSpans(text)
        const markers = spans.length > 0 ? collectMarkerPositions(spans) : null

        // Proportional: Bold-aware Format-Runs
        let fullParsed = null
        if (markers && this.renderer && this.renderer.isProportional()) {
            fullParsed = this._getCachedParsed(cacheKey, text, null, baseStyle)
        }

        // Text an \n splitten (normalerweise nur bei Paragraphen mit Soft-Breaks)
        const segments = text.split('\n')
        const result = []
        let textOffset = 0

        for (let s = 0; s < segments.length; s++) {
            const segment = segments[s]
            const linePrefix = (result.length === 0) ? prefix : indent
            const lineWidth = width - linePrefix.length
            if (lineWidth <= 0) continue

            // heightQ basierend auf Block-Typ (wird in _computeSpacing noch mal gesetzt,
            // aber hier schon für korrekten scaleFactor beim Word-Wrap gebraucht)
            const blockHeightQ = blockType === 'code' ? LINE_HEIGHT_Q.CODE : LINE_HEIGHT_Q.NORMAL
            const wrapped = this._wrapText(segment, lineWidth, markers, textOffset, fullParsed, blockHeightQ)

            // Leeres Segment → eine leere Zeile
            if (wrapped.length === 0) {
                const currentPrefix = (result.length === 0) ? prefix : indent
                const line = new ConsoleLine({
                    entryIndex, startOffset: textOffset, endOffset: textOffset,
                    lineType: 'AI', isFirstLine: result.length === 0,
                    text: '', prefix: currentPrefix
                })
                line.blockType = blockType
                line.blockId = blockIndex
                line.blockText = text
                line.blockCacheKey = cacheKey
                result.push(line)
            }

            for (let w = 0; w < wrapped.length; w++) {
                const isFirst = (result.length === 0)
                const currentPrefix = isFirst ? prefix : indent
                const line = new ConsoleLine({
                    entryIndex,
                    startOffset: textOffset + wrapped[w].start,
                    endOffset: textOffset + wrapped[w].end,
                    lineType: 'AI',
                    isFirstLine: isFirst,
                    text: wrapped[w].text,
                    prefix: currentPrefix
                })
                if (wrapped[w].pixelWidth !== undefined) {
                    line.pixelWidth = wrapped[w].pixelWidth
                }
                line.blockType = blockType
                line.blockId = blockIndex
                line.blockText = text
                line.blockCacheKey = cacheKey
                result.push(line)
            }

            textOffset += segment.length + (s < segments.length - 1 ? 1 : 0)
        }

        if (result.length === 0) {
            const line = new ConsoleLine({
                entryIndex, startOffset: 0, endOffset: 0,
                lineType: 'AI', isFirstLine: true, text: '', prefix: prefix
            })
            line.blockType = blockType
            line.blockId = blockIndex
            line.blockText = text
            line.blockCacheKey = cacheKey
            result.push(line)
        }

        return result
    }

    /**
     * Erzeugt ConsoleLines fuer einen Code-Block.
     * Jede Zeile wird einzeln umbrochen, ohne Markdown-Parsing.
     * @param {string} text - Code-Text (mit \n)
     * @param {number} entryIndex
     * @param {number} blockIndex
     * @param {number} width
     * @returns {ConsoleLine[]}
     */
    _wrapCodeBlock(text, entryIndex, blockIndex, width) {
        const codeIndent = '  '
        // Proportional: 6QL-Mono-Zeichen sind breiter als Grid-charWidth
        // → weniger Zeichen pro Zeile (width * 4 / heightQ)
        const effectiveWidth = (this.renderer && this.renderer.isProportional())
            ? Math.floor(width * 4 / LINE_HEIGHT_Q.CODE)
            : width
        const codeWidth = effectiveWidth - codeIndent.length
        if (codeWidth <= 0) return []

        const codeLines = text.split('\n')
        const result = []
        let textOffset = 0

        for (let c = 0; c < codeLines.length; c++) {
            const codeLine = codeLines[c]

            // Harter Umbruch bei Zeilenbreite (kein Word-Wrap fuer Code)
            let pos = 0
            while (pos < codeLine.length || (pos === 0 && codeLine.length === 0)) {
                const end = Math.min(pos + codeWidth, codeLine.length)
                const lineText = codeLine.substring(pos, end)
                const line = new ConsoleLine({
                    entryIndex,
                    startOffset: textOffset + pos,
                    endOffset: textOffset + end,
                    lineType: 'AI',
                    isFirstLine: result.length === 0,
                    text: lineText,
                    prefix: codeIndent
                })
                line.blockType = 'code'
                line.blockId = blockIndex
                line.blockText = text
                line.blockCacheKey = `${entryIndex}_b${blockIndex}`
                line.forceMonospace = true
                result.push(line)

                pos = end
                if (pos === 0 && codeLine.length === 0) break
            }

            textOffset += codeLine.length + (c < codeLines.length - 1 ? 1 : 0)
        }

        if (result.length === 0) {
            const line = new ConsoleLine({
                entryIndex, startOffset: 0, endOffset: 0,
                lineType: 'AI', isFirstLine: true, text: '', prefix: codeIndent
            })
            line.blockType = 'code'
            line.blockId = blockIndex
            line.blockText = text
            line.forceMonospace = true
            result.push(line)
        }

        return result
    }

    // === Quarter-Line Spacing ===

    /**
     * Prueft ob Quarter-Line Spacing aktiv ist (nur Proportional-Mode).
     */
    _useQuarterSpacing() {
        return this.renderer && this.renderer.isProportional()
    }

    /**
     * Berechnet spacingBefore fuer alle Zeilen und baut _lineQuarterY auf.
     * Wird am Ende von rebuildLines() aufgerufen.
     */
    _computeSpacing() {
        this._lineQuarterY = new Array(this.lines.length)
        if (this.lines.length === 0) {
            this._totalQuarters = 0
            return
        }

        if (!this._useQuarterSpacing()) {
            // Monospace: kein Spacing, lineQuarterY = i * 4
            for (let i = 0; i < this.lines.length; i++) {
                this.lines[i].spacingBefore = 0
                this._lineQuarterY[i] = i * 4
            }
            this._totalQuarters = this.lines.length * 4
            return
        }

        // Proportional: spacingBefore basierend auf Block-/Entry-Typ
        let prevEntryIndex = -999
        let prevBlockId = -999
        let prevBlockType = null

        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i]

            if (i === 0) {
                // Erste Zeile
                line.spacingBefore = 0
            } else if (line.lineType === 'BLANK') {
                // BLANK-Zeile (sollte in Proportional nicht vorkommen)
                line.spacingBefore = 0
            } else if (line.entryIndex !== prevEntryIndex && prevEntryIndex >= 0 && line.entryIndex >= 0) {
                // Entry-Wechsel
                line.spacingBefore = PARAGRAPH_SPACING.ENTRY_GAP
            } else if (line.lineType === 'INPUT' && prevEntryIndex >= 0 && line.entryIndex < 0) {
                // Erste Input-Zeile nach Entries
                line.spacingBefore = PARAGRAPH_SPACING.ENTRY_GAP
            } else if (line.blockId >= 0 && line.blockId !== prevBlockId && prevBlockId >= 0) {
                // Block-Wechsel innerhalb eines AI-Entry: Margin-Collapsing
                line.spacingBefore = this._blockSpacing(line.blockType, prevBlockType)
            } else if (line.blockId >= 0 && line.blockId === prevBlockId) {
                // Wrap-Continuation innerhalb eines Blocks
                line.spacingBefore = 0
            } else {
                line.spacingBefore = 0
            }

            // Tracking
            if (line.entryIndex !== prevEntryIndex) {
                prevEntryIndex = line.entryIndex
                prevBlockId = -999
                prevBlockType = null
            }
            if (line.blockId >= 0) {
                if (line.blockId !== prevBlockId) {
                    prevBlockType = line.blockType
                }
                prevBlockId = line.blockId
            }
        }

        // heightQ pro Zeile bestimmen (nur Proportional)
        for (let i = 0; i < this.lines.length; i++) {
            const line = this.lines[i]
            if (line.lineType === 'THINKING') {
                line.heightQ = LINE_HEIGHT_Q.THINKING
            } else if (line.lineType === 'SYSTEM' || line.lineType === 'ERROR') {
                line.heightQ = LINE_HEIGHT_Q.SYSTEM
            } else if (line.blockType === 'heading' && line.blockLevel > 0) {
                line.heightQ = headingHeightQ(line.blockLevel)
            } else if (line.forceMonospace || line.blockType === 'code') {
                line.heightQ = LINE_HEIGHT_Q.CODE
            } else {
                line.heightQ = LINE_HEIGHT_Q.NORMAL
            }
        }

        // Kumulatives Positions-Array aufbauen
        let q = 0
        for (let i = 0; i < this.lines.length; i++) {
            q += this.lines[i].spacingBefore
            this._lineQuarterY[i] = q
            q += this.lines[i].heightQ
        }
        this._totalQuarters = q
    }

    /**
     * Bestimmt den Spacing-Wert zwischen zwei aufeinanderfolgenden Bloecken
     * mittels Margin-Collapsing: max(prev.after, curr.before).
     * @param {string} currentType - Typ des aktuellen Blocks
     * @param {string} prevType - Typ des vorherigen Blocks
     * @returns {number} Spacing in Vierteln
     */
    _blockSpacing(currentType, prevType) {
        const prevSpacing = this._blockTypeSpacing(prevType)
        const currSpacing = this._blockTypeSpacing(currentType)
        return collapseSpacing(prevSpacing, currSpacing)
    }

    /**
     * Gibt die Spacing-Definition fuer einen Block-Typ zurueck.
     * @param {string} blockType - 'heading', 'list', 'code', 'blockquote', 'hr', 'paragraph'
     * @returns {{ before: number, after: number }}
     */
    _blockTypeSpacing(blockType) {
        if (blockType === 'heading') return PARAGRAPH_SPACING.NORMAL  // Console-Headings: Normal-Spacing
        if (blockType === 'hr') return PARAGRAPH_SPACING.HR
        if (blockType === 'code') return PARAGRAPH_SPACING.CODE_BLOCK
        if (blockType === 'list') return PARAGRAPH_SPACING.LIST
        if (blockType === 'blockquote') return PARAGRAPH_SPACING.BLOCKQUOTE
        return PARAGRAPH_SPACING.NORMAL
    }

    /**
     * Berechnet die Pixel-Y-Position einer Zeile auf dem Screen.
     * @param {number} lineIndex
     * @returns {number} Pixel-Position (kann negativ sein)
     */
    _lineScreenPixelY(lineIndex) {
        if (!this._useQuarterSpacing() || this._lineQuarterY.length === 0) {
            return (lineIndex - this.viewportStart) * this.renderer.charHeight
        }
        const quarterH = this.renderer.charHeight / 4
        const vpQ = this._lineQuarterY[this.viewportStart] || 0
        const lineQ = this._lineQuarterY[lineIndex] || 0
        return (lineQ - vpQ) * quarterH - this._viewportPixelOffset
    }

    /**
     * Findet den Zeilen-Index an einer Screen-Y-Position (Pixel).
     * @param {number} screenPixelY
     * @returns {number} Zeilen-Index
     */
    _lineAtScreenPixelY(screenPixelY) {
        if (!this._useQuarterSpacing() || this._lineQuarterY.length === 0) {
            return this.viewportStart + Math.floor(screenPixelY / this.renderer.charHeight)
        }
        const quarterH = this.renderer.charHeight / 4
        const vpQ = this._lineQuarterY[this.viewportStart] || 0
        const targetQ = vpQ + (screenPixelY + this._viewportPixelOffset) / quarterH

        // Binaersuche
        let lo = 0, hi = this._lineQuarterY.length - 1
        while (lo < hi) {
            const mid = (lo + hi + 1) >> 1
            if (this._lineQuarterY[mid] <= targetQ) lo = mid
            else hi = mid - 1
        }
        return lo
    }

    /**
     * Scrollt pixelgenau — funktioniert in Proportional- UND Monospace-Mode.
     * Im Proportional: Sub-Pixel-Offset in _viewportPixelOffset.
     * Im Monospace: grid-based, Sub-Zeilen-Input in _monoScrollAccumulator gesammelt.
     * @param {number} deltaPx - Pixel-Betrag (positiv = nach unten)
     */
    scrollByPixels(deltaPx) {
        // Monospace braucht nur eine Zeilenhöhen-Referenz — Fallback aus TIMING (Tests ohne renderer)
        const charHeight = this.renderer?.charHeight || TIMING.scrollPixelsPerLine
        if (charHeight <= 0) return

        const useQuarter = this._useQuarterSpacing() && this._lineQuarterY.length > 0

        if (!useQuarter) {
            // Monospace: grid-based, zeilenweise scrollen
            this._monoScrollAccumulator += deltaPx
            const lineDelta = Math.trunc(this._monoScrollAccumulator / charHeight)
            if (lineDelta !== 0) {
                this._monoScrollAccumulator -= lineDelta * charHeight
                this.scrollBy(lineDelta)
            }
            return
        }

        // Proportional: Pixel-genaues Scrolling mit Sub-Pixel-Offset (braucht renderer)
        if (!this.renderer) return
        const quarterH = charHeight / 4
        this._viewportPixelOffset += deltaPx

        // Normalisieren: viewportStart vorwaerts
        while (this.viewportStart < this.lines.length - 1) {
            const lineH = (this.lines[this.viewportStart].heightQ || 4) * quarterH
            const nextSpacingPx = (this.lines[this.viewportStart + 1]?.spacingBefore || 0) * quarterH
            const distToNextTop = lineH + nextSpacingPx
            if (this._viewportPixelOffset < distToNextTop) break
            this.viewportStart++
            this._viewportPixelOffset -= distToNextTop
        }

        // Normalisieren: viewportStart rueckwaerts
        while (this._viewportPixelOffset < 0 && this.viewportStart > 0) {
            const spacingPx = (this.lines[this.viewportStart]?.spacingBefore || 0) * quarterH
            const prevLineH = (this.lines[this.viewportStart - 1]?.heightQ || 4) * quarterH
            this._viewportPixelOffset += (prevLineH + spacingPx)
            this.viewportStart--
        }

        // Raender clampen
        if (this.viewportStart <= 0 && this._viewportPixelOffset < 0) {
            this.viewportStart = 0
            this._viewportPixelOffset = 0
        }

        // Quarter-basiertes Max: letzte Zeile soll gerade noch sichtbar sein
        const viewportQ = this.height * 4
        if (this._totalQuarters <= viewportQ) {
            // Alles passt rein — kein Scrollen noetig
            this.viewportStart = 0
            this._viewportPixelOffset = 0
        } else {
            // Pruefen ob Viewport ueber das Ende hinausscrollt
            const vpQ = this._lineQuarterY[this.viewportStart] || 0
            const currentTopQ = vpQ + this._viewportPixelOffset / quarterH
            const maxTopQ = this._totalQuarters - viewportQ
            if (currentTopQ > maxTopQ) {
                // Zurueck auf scrollToBottom-Position setzen
                this.scrollToBottom()
            }
        }
    }

    /**
     * Legacy-Wrapper: Scrollt um Viertel-Zeilenhoehen.
     * Leitet an scrollByPixels weiter — dort liegt die eigentliche Scroll-Mechanik.
     * @param {number} deltaQ - Anzahl Viertel-Zeilen (positiv = nach unten)
     */
    scrollByQ(deltaQ) {
        if (!this.renderer) return
        this.scrollByPixels(deltaQ * this.renderer.charHeight / 4)
    }

    // === Coordinate Mapping ===

    /**
     * Rechnet einen Input-Offset in Screen-Koordinaten um.
     * @param {number} inputOffset - Position im inputText
     * @returns {{x: number, y: number, pixelX?: number}|null} Screen-Position relativ zum Pane
     */
    inputToScreen(inputOffset) {
        const startIdx = this._firstInputLineIndex >= 0 ? this._firstInputLineIndex : 0

        for (let i = startIdx; i < this.lines.length; i++) {
            const line = this.lines[i]
            if (line.lineType !== 'INPUT') continue

            if (inputOffset >= line.startOffset && inputOffset <= line.endOffset) {
                if (inputOffset === line.endOffset && i + 1 < this.lines.length &&
                    this.lines[i + 1].lineType === 'INPUT' &&
                    this.lines[i + 1].startOffset === inputOffset) {
                    continue
                }
                // Im RT-Modus: Display = Data, keine Marker
                // Kumulative Display-Spalten berechnen (Emoji = 2 Spalten)
                const inputText = this.model.getInputText()
                let displayX = 0
                for (const ch of inputText.substring(line.startOffset, inputOffset)) {
                    displayX += charDisplayWidth(ch)
                }
                const x = line.prefix.length + displayX
                // Quarter-Spacing: fraktionale Y-Position
                const screenY = this._useQuarterSpacing() && this.renderer
                    ? this._lineScreenPixelY(i) / this.renderer.charHeight
                    : i - this.viewportStart
                const result = { x, y: screenY, lineIndex: i, heightQ: line.heightQ || 4 }

                // Proportional: pixelX hinzufuegen
                const posMap = this._getPositionMap(i)
                if (posMap) {
                    const prefixPx = line.prefix.length * this.renderer.charWidth
                    result.pixelX = prefixPx +
                        (displayX < posMap.xPositions.length
                            ? posMap.xPositions[displayX]
                            : posMap.totalWidth)
                }

                return result
            }
        }

        // Fallback: letzte Input-Zeile, Ende
        for (let i = this.lines.length - 1; i >= startIdx; i--) {
            if (this.lines[i].lineType === 'INPUT') {
                const line = this.lines[i]
                // Kumulative Display-Spalten (Emoji = 2 Spalten)
                let displayLen = 0
                for (const ch of line.text) {
                    displayLen += charDisplayWidth(ch)
                }
                const x = line.prefix.length + displayLen
                const screenY = this._useQuarterSpacing() && this.renderer
                    ? this._lineScreenPixelY(i) / this.renderer.charHeight
                    : i - this.viewportStart
                const result = { x, y: screenY, lineIndex: i, heightQ: line.heightQ || 4 }

                const posMap = this._getPositionMap(i)
                if (posMap) {
                    const prefixPx = line.prefix.length * this.renderer.charWidth
                    result.pixelX = prefixPx + posMap.totalWidth
                }

                return result
            }
        }

        return null
    }

    /**
     * Rechnet Screen-Koordinaten in einen Input-Offset um.
     * @param {number} screenX - X-Position relativ zum Pane
     * @param {number} screenY - Y-Position relativ zum Pane
     * @param {number|null} pixelX - Pixel-Position (proportional), null = Mono
     * @returns {number|null} Input-Offset oder null wenn nicht im Input
     */
    screenToInput(screenX, screenY, pixelX = null, pixelY = null) {
        let lineIndex
        if (this._useQuarterSpacing() && this.renderer) {
            // pixelY ist exakt; screenY*charHeight wäre nur bei einheitlichen
            // Zeilenhöhen korrekt — im Quarter-Spacing-Mode ergeben Zeilen mit
            // unterschiedlichem heightQ Y-Versatz, der den Hit-Bereich nach unten
            // verschiebt (Klick am oberen Rand der nächsten Zeile trifft die
            // vorherige). Fallback bleibt für Tests ohne pixelY.
            const screenPixelY = pixelY != null
                ? pixelY
                : screenY * this.renderer.charHeight
            lineIndex = this._lineAtScreenPixelY(screenPixelY)
        } else {
            lineIndex = this.viewportStart + screenY
        }
        if (lineIndex < 0 || lineIndex >= this.lines.length) return null

        const line = this.lines[lineIndex]
        if (line.lineType !== 'INPUT') return null

        // Proportional: pixel-basierte Aufloesung
        const posMap = this._getPositionMap(lineIndex)
        if (posMap && pixelX !== null) {
            const textPixelX = pixelX - line.prefix.length * this.renderer.charWidth
            const displayIndex = posMap.getCharIndexAtX(Math.max(0, textPixelX))
            return this._displayIndexToDataOffset(line, displayIndex)
        }

        const textX = screenX - line.prefix.length
        if (textX <= 0) return line.startOffset

        // RT-Modus: Kumulative Display-Breite → Daten-Offset (Emoji = 2 Spalten)
        let col = 0
        let dataPos = line.startOffset
        for (const ch of line.text) {
            const w = charDisplayWidth(ch)
            if (col + w > textX) break
            col += w
            dataPos += ch.length  // UTF-16 Schrittweite
        }
        return Math.min(dataPos, this.model.getInputText().length)
    }

    /**
     * Rechnet Screen-Koordinaten in Entry + Block + Offset um (fuer Output-Selection).
     * Setzt zusaetzlich `inPrefix=true`, wenn der Klick im Prefix-Bereich liegt
     * (THINKING '[...] ' bzw. Spacing-Prefix bei Folgezeilen). CoreInput nutzt
     * das, um Klicks auf den THINKING-Marker als Toggle zu interpretieren.
     * `blockId` ist `-1` fuer non-AI-Entries (offset entry-relativ), bei AI ist
     * es der Block-Index aus `parseMarkdownBlocks` (offset block-relativ).
     * @param {number} screenX
     * @param {number} screenY
     * @param {number|null} pixelX - Pixel-Position (proportional)
     * @returns {{entryIndex: number, blockId: number, offset: number, inPrefix: boolean}|null}
     */
    screenToEntry(screenX, screenY, pixelX = null, pixelY = null) {
        let lineIndex
        if (this._useQuarterSpacing() && this.renderer) {
            // pixelY-Pfad: siehe Begründung in screenToInput.
            const screenPixelY = pixelY != null
                ? pixelY
                : screenY * this.renderer.charHeight
            lineIndex = this._lineAtScreenPixelY(screenPixelY)
        } else {
            lineIndex = this.viewportStart + screenY
        }
        if (lineIndex < 0 || lineIndex >= this.lines.length) return null

        const line = this.lines[lineIndex]
        if (line.entryIndex < 0) return null  // Input oder Blank

        // Prefix-Hit erkennen: Char-Spalte vor Prefix-Ende = Klick auf Marker.
        const inPrefix = (screenX < line.prefix.length)
        const blockId = line.blockId

        // Proportional: pixel-basierte Aufloesung fuer USER/AI Entries
        const posMap = this._getPositionMap(lineIndex)
        if (posMap && pixelX !== null) {
            const textPixelX = pixelX - line.prefix.length * this.renderer.charWidth
            const displayIndex = posMap.getCharIndexAtX(Math.max(0, textPixelX))
            const offset = this._displayIndexToDataOffset(line, displayIndex)
            return { entryIndex: line.entryIndex, blockId, offset, inPrefix }
        }

        const textX = screenX - line.prefix.length
        if (textX < 0) return { entryIndex: line.entryIndex, blockId, offset: line.startOffset, inPrefix }
        return {
            entryIndex: line.entryIndex,
            blockId,
            offset: line.startOffset + Math.min(textX, line.text.length),
            inPrefix
        }
    }

    /**
     * Konvertiert einen Display-Index in einen Data-Offset innerhalb einer Zeile.
     * @param {ConsoleLine} line
     * @param {number} displayIndex
     * @returns {number} Data-Offset
     */
    _displayIndexToDataOffset(line, displayIndex) {
        const lineType = line.lineType

        // Input im RT-Modus: Display = Data (keine Marker)
        if (lineType === 'INPUT') {
            return Math.min(line.startOffset + displayIndex, line.endOffset)
        }

        let fullText
        if (line.blockText !== null) {
            fullText = line.blockText
        } else {
            fullText = (line.entryIndex >= 0 ? (this.model.getEntries()[line.entryIndex] || {}).text : null)
        }
        if (!fullText) return line.startOffset

        const needsFormat = lineType === 'USER' || lineType === 'AI'
        if (!needsFormat) {
            // Kein Markdown → Display = Data
            return Math.min(line.startOffset + displayIndex, line.endOffset)
        }

        const cacheKey = line.blockText !== null
            ? (line.blockCacheKey || `${line.entryIndex}_b${line.blockId}`)
            : line.entryIndex
        const blockBaseStyle = line.blockText !== null ? this._getBlockBaseStyle(line.blockType) : null
        const parsed = this._getCachedParsed(cacheKey, fullText, lineType, blockBaseStyle)

        // Zeilen-Slice: nur Display-Zeichen im Offset-Bereich
        let displayCount = 0
        for (const item of parsed) {
            if (item.dataOffset >= line.endOffset) break
            if (item.dataOffset >= line.startOffset) {
                if (displayCount === displayIndex) return item.dataOffset
                displayCount++
            }
        }
        return line.endOffset
    }

    /**
     * Zaehlt sichtbare Zeichen (ohne Marker) in einem Data-Offset-Bereich.
     * @param {number} from - Start Data-Offset (inklusiv)
     * @param {number} to - Ende Data-Offset (exklusiv)
     * @param {object|null} mapping - Display-Mapping
     * @returns {number}
     */
    // === PositionMap Cache ===

    /**
     * Gibt die LinePositionMap fuer eine Zeile zurueck (lazy, gecacht).
     * @param {number} lineIndex - Zeilen-Index in this.lines
     * @returns {LinePositionMap|null} null wenn nicht proportional
     */
    _getPositionMap(lineIndex) {
        if (!this.renderer || !this.renderer.isProportional()) return null
        // heightQ der Zeile bestimmt den MetricsCache (System-Font bei 4QL)
        const lineHeightQForCache = this.lines[lineIndex]?.heightQ || 4
        const metricsCache = this.renderer.getGlyphMetricsCache(lineHeightQForCache)
        if (!metricsCache) return null

        let map = this._positionMaps.get(lineIndex)
        if (map) return map

        const line = this.lines[lineIndex]
        if (!line || line.lineType === 'BLANK') return null

        // Parsed Markdown fuer den Gesamttext holen
        const lineType = line.lineType
        const needsFormat = lineType === 'INPUT' || lineType === 'USER' || lineType === 'AI'

        // Code-Bloecke: kein proportionales Rendering
        if (line.forceMonospace || line.blockType === 'code') return null

        let fullText
        let blockBaseStyle = null
        if (line.blockText !== null) {
            fullText = line.blockText
            blockBaseStyle = this._getBlockBaseStyle(line.blockType)
        } else if (lineType === 'INPUT') {
            fullText = this.model.getInputText()
        } else if (line.entryIndex >= 0) {
            const entry = this.model.getEntries()[line.entryIndex]
            fullText = entry ? entry.text : ''
        }
        if (!fullText && fullText !== '') return null

        let parsed
        if (needsFormat) {
            if (lineType === 'INPUT') {
                // Input: RT-basiert (wie Editor), konsistent mit Word-Wrap und Rendering
                const formats = this.model.getInputFormats()
                parsed = this._getCachedInputParsed(fullText, formats)
            } else {
                const cacheKey = line.blockText !== null
                    ? (line.blockCacheKey || `${line.entryIndex}_b${line.blockId}`)
                    : line.entryIndex
                parsed = this._getCachedParsed(cacheKey, fullText, lineType, blockBaseStyle)
            }
        } else {
            // SYSTEM/ERROR: kein Markdown → einfaches Parsed-Array
            const baseStyle = ENTRY_STYLES[lineType] || Style.CONSOLE_SYSTEM
            parsed = []
            for (let i = 0; i < fullText.length; i++) {
                parsed.push({ char: fullText[i], style: baseStyle, dataOffset: i })
            }
        }

        // Zeilen-Slice: nur Display-Zeichen im Offset-Bereich
        const lineChars = []
        for (const item of parsed) {
            if (item.dataOffset >= line.startOffset && item.dataOffset < line.endOffset) {
                lineChars.push(item)
            }
        }

        const lineText = parsedCharsToString(lineChars)
        const lineFormatRuns = parsedToFormatRuns(lineChars)
        const enrichedRuns = enrichFormatRuns(lineFormatRuns)

        const rawLayout = computeStringLayout(lineText, metricsCache, metricsCache.baseStep, enrichedRuns)
        const lineHeightQ = line.heightQ || 4
        const lineScaleFactor = this.renderer.isProportional()
            ? this.renderer.getScaleFactor(lineHeightQ) * lineHeightQ / 4
            : this.renderer.getScaleFactor()
        // Mono-Spacing fuer Inline-Code-Spans (Style.CONSOLE_AI_CODE / formatRun.mono)
        const monoCharWidth = this.renderer.charWidth * lineHeightQ / 4
        map = buildLinePositionMap(lineText, enrichedRuns, metricsCache, lineScaleFactor, rawLayout, monoCharWidth)
        map._rawLayout = rawLayout
        this._positionMaps.set(lineIndex, map)
        return map
    }

    // === Viewport ===

    /**
     * Scrollt ans Ende (Input sichtbar).
     */
    scrollToBottom() {
        if (this._useQuarterSpacing() && this.renderer && this._totalQuarters > 0) {
            // Quarter-basiert: finde viewportStart so dass letzte Zeile sichtbar ist
            const viewportH = this.height * 4  // Viewport-Hoehe in Vierteln
            if (this._totalQuarters <= viewportH) {
                this.viewportStart = 0
                this._viewportPixelOffset = 0
                return
            }
            // Rueckwaerts suchen: Zeile finden deren quarterY + 4 <= totalQ - (totalQ - viewportH)
            const targetQ = this._totalQuarters - viewportH
            let lo = 0, hi = this._lineQuarterY.length - 1
            while (lo < hi) {
                const mid = (lo + hi + 1) >> 1
                if (this._lineQuarterY[mid] <= targetQ) lo = mid
                else hi = mid - 1
            }
            this.viewportStart = lo
            // Pixel-Offset feintunen
            const quarterH = this.renderer.charHeight / 4
            this._viewportPixelOffset = (this._lineQuarterY[lo] < targetQ)
                ? (targetQ - this._lineQuarterY[lo]) * quarterH
                : 0
        } else {
            const overflow = this.lines.length - this.height
            this.viewportStart = Math.max(0, overflow)
        }
    }

    /**
     * Scrollt um delta Zeilen.
     * @param {number} delta - Positiv = nach unten, negativ = nach oben
     */
    scrollBy(delta) {
        if (this._useQuarterSpacing() && this.renderer) {
            this.scrollByQ(delta * 4)
            return
        }
        const maxStart = Math.max(0, this.lines.length - this.height)
        this.viewportStart = Math.max(0, Math.min(this.viewportStart + delta, maxStart))
    }

    /**
     * Stellt sicher dass der Cursor sichtbar ist.
     */
    ensureCursorVisible() {
        const cursorPos = this.inputToScreen(this.model.getInputCursor())
        if (!cursorPos) {
            this.scrollToBottom()
            return
        }

        if (this._useQuarterSpacing() && this.renderer) {
            // Proportional: pixelbasierte Pruefung — Zeilenhoehe richtet sich nach heightQ der Cursor-Zeile
            // (USER-Input ist 6QL, nicht 4QL — sonst scrollen wir zu wenig nach unten und schneiden ab)
            const quarterH = this.renderer.charHeight / 4
            const lineH = (cursorPos.heightQ || 4) * quarterH
            const cursorPxY = cursorPos.y * this.renderer.charHeight
            const viewportPx = this.height * this.renderer.charHeight
            if (cursorPxY < 0) {
                this.scrollByQ(Math.floor(cursorPxY / quarterH))
            } else if (cursorPxY + lineH > viewportPx) {
                const overPx = cursorPxY + lineH - viewportPx
                this.scrollByQ(Math.ceil(overPx / quarterH))
            }
        } else {
            if (cursorPos.y < 0) {
                this.viewportStart += cursorPos.y
            } else if (cursorPos.y >= this.height) {
                this.viewportStart += cursorPos.y - this.height + 1
            }
        }
    }

    // === Rendering ===

    /**
     * Rendert den Console-Inhalt.
     * @param {number} offsetX - Pane-Offset X
     * @param {number} offsetY - Pane-Offset Y
     * @param {function} setCharFn - (x, y, char, style, isSelected, highlightType)
     */
    render(offsetX, offsetY, setCharFn) {
        if (this._useQuarterSpacing() && this.renderer) {
            // Proportional mit Quarter-Spacing: fraktionale Y-Positionen
            const viewportEndPx = this.height * this.renderer.charHeight
            const quarterH = this.renderer.charHeight / 4
            for (let i = this.viewportStart; i < this.lines.length; i++) {
                const pxY = this._lineScreenPixelY(i)
                if (pxY >= viewportEndPx) break
                const lineH = (this.lines[i].heightQ || 4) * quarterH
                if (pxY + lineH <= 0) continue
                const fractionalY = pxY / this.renderer.charHeight
                const line = this.lines[i]
                const absY = offsetY + fractionalY

                // Im Proportional-Modus: ALLE Zeilen proportional rendern (auch forceMonospace)
                // damit Code-Blöcke und Prefixe in der richtigen heightQ-Größe erscheinen
                if (line.lineType === 'BLANK') {
                    // skip
                } else {
                    this._renderLineProportional(line, i, offsetX, absY, setCharFn)
                }
            }
        } else {
            // Monospace: ganzzahlige Y-Positionen
            for (let screenY = 0; screenY < this.height; screenY++) {
                const lineIndex = this.viewportStart + screenY
                if (lineIndex >= this.lines.length) break

                const line = this.lines[lineIndex]
                const absY = offsetY + screenY

                if (this.renderer && this.renderer.isProportional() && line.pixelWidth > 0) {
                    this._renderLineProportional(line, lineIndex, offsetX, absY, setCharFn)
                    continue
                }

                this._renderLine(line, offsetX, absY, setCharFn)
            }
        }
    }

    /**
     * Rendert eine einzelne Zeile mit WYSIWYG-Formatierung (Mono).
     * @param {ConsoleLine} line
     * @param {number} offsetX
     * @param {number} absY
     * @param {function} setCharFn
     */
    _renderLine(line, offsetX, absY, setCharFn) {
        if (line.lineType === 'BLANK') return

        // Selection-Range fuer Input- ODER Output-Zeilen berechnen
        let selStart = -1, selEnd = -1
        const selRangeData = this._getSelectionRangeForLine(line)
        if (selRangeData) {
            selStart = selRangeData.start
            selEnd = selRangeData.end
        }

        const baseStyle = this._getLineStyle(line)
        let x = offsetX

        // Prefix rendern (mit eigenem Style fuer Prompt)
        // THINKING: Prefix '[...] ' bleibt normal; nur der Text darunter ist kursiv.
        const prefixStyle = (line.lineType === 'INPUT')
            ? (this.model.isLocked() ? Style.CONSOLE_LOCK : Style.CONSOLE_PROMPT)
            : (line.lineType === 'THINKING' ? Style.CONSOLE_THINKING_COLLAPSED : baseStyle)
        for (let i = 0; i < line.prefix.length; i++) {
            setCharFn(x++, absY, line.prefix[i], prefixStyle, false, null)
        }

        // Code-Bloecke: kein Markdown, eigener Style
        if (line.blockType === 'code') {
            const codeStyle = Style.CONSOLE_AI_CODE
            for (const ch of line.text) {
                setCharFn(x, absY, ch, codeStyle, false, null)
                x += charDisplayWidth(ch)
            }
            return
        }

        // URL-Spans fuer Link-Highlighting (alle Entry-Typen)
        const textInfo = this._getLineTextInfo(line)
        const urlSpans = textInfo ? this._getUrlSpans(textInfo.fullText, textInfo.cacheKey) : null
        const hasUrls = urlSpans && urlSpans.length > 0

        // Text rendern — WYSIWYG fuer Input (RT), USER und AI Entries (Markdown)
        const needsFormatting = line.lineType === 'INPUT' || line.lineType === 'USER' || line.lineType === 'AI'
        if (needsFormatting) {
            let fullText
            let parsed

            if (line.lineType === 'INPUT') {
                // Input: RT-basiertes Rendering
                fullText = this.model.getInputText()
                const formats = this.model.getInputFormats()
                parsed = this._getCachedInputParsed(fullText, formats)
            } else if (line.blockText !== null) {
                fullText = line.blockText
                const cacheKey = line.blockCacheKey || `${line.entryIndex}_b${line.blockId}`
                const blockBaseStyle = this._getBlockBaseStyle(line.blockType)
                parsed = this._getCachedParsed(cacheKey, fullText, line.lineType, blockBaseStyle)
            } else if (line.entryIndex >= 0) {
                fullText = this.model.getEntries()[line.entryIndex].text
                parsed = this._getCachedParsed(line.entryIndex, fullText, line.lineType)
            }

            if (parsed) {
                for (const item of parsed) {
                    if (item.dataOffset >= line.endOffset) break
                    if (item.dataOffset >= line.startOffset) {
                        const isSelected = (selStart >= 0 && item.dataOffset >= selStart && item.dataOffset < selEnd)
                        const style = (hasUrls && getUrlSpanAtOffset(item.dataOffset, urlSpans))
                            ? Style.CONSOLE_LINK : item.style
                        setCharFn(x, absY, item.char, style, isSelected, null)
                        x += charDisplayWidth(item.char)
                    }
                }
                return
            }
        }

        // Fallback: Einfaches Rendering ohne Formatierung (SYSTEM, ERROR)
        const textStyle = (line.lineType === 'INPUT') ? Style.CONSOLE_INPUT : baseStyle
        let dataOffset = line.startOffset
        for (const ch of line.text) {
            const style = (hasUrls && getUrlSpanAtOffset(dataOffset, urlSpans))
                ? Style.CONSOLE_LINK : textStyle
            const isSelected = (selStart >= 0 && dataOffset >= selStart && dataOffset < selEnd)
            setCharFn(x, absY, ch, style, isSelected, null)
            x += charDisplayWidth(ch)
            dataOffset++
        }
    }

    /**
     * Rendert eine Zeile proportional.
     * Prefix bleibt grid-basiert, Text wird ueber renderProportionalLine() gerendert.
     * @param {ConsoleLine} line
     * @param {number} lineIndex
     * @param {number} offsetX
     * @param {number} absY
     * @param {function} setCharFn
     */
    _renderLineProportional(line, lineIndex, offsetX, absY, setCharFn) {
        if (line.lineType === 'BLANK') return

        // Collapsed THINKING: '[...]' direkt rendern (eigenstaendiger Text, nicht aus Entry)
        if (line.lineType === 'THINKING' && line.startOffset === line.endOffset && line.text) {
            const style = this._getLineStyle(line)
            const heightQ = line.heightQ || 4
            const charWidth = this.renderer.charWidth
            const charHeight = this.renderer.charHeight
            const textStartX = offsetX * charWidth
            const pixelY = absY * charHeight
            const formatRuns = [{ start: 0, end: line.text.length, bold: false, italic: false,
                underline: false, strikethrough: false, style }]
            this.renderer.renderProportionalLine(
                this.renderer.ctx, line.text, textStartX, pixelY,
                formatRuns, null, null, null, heightQ
            )
            return
        }

        const baseStyle = this._getLineStyle(line)
        let x = offsetX

        // Prefix rendern
        // THINKING: Prefix '[...] ' bleibt normal; nur der Text darunter ist kursiv.
        const prefixStyle = (line.lineType === 'INPUT')
            ? (this.model.isLocked() ? Style.CONSOLE_LOCK : Style.CONSOLE_PROMPT)
            : (line.lineType === 'THINKING' ? Style.CONSOLE_THINKING_COLLAPSED : baseStyle)
        if (line.prefix.length > 0 && this.renderer.isProportional()) {
            // Proportional: Prefix als zusammenhängenden String in heightQ-Größe rendern
            const heightQ = line.heightQ || 4
            const charHeight = this.renderer.charHeight
            const prefixStartX = x * this.renderer.charWidth
            const pixelY = absY * charHeight
            this.renderer.renderProportionalLine(
                this.renderer.ctx, line.prefix, prefixStartX, pixelY,
                [{ start: 0, end: line.prefix.length, bold: false, italic: false, underline: false, strikethrough: false, style: prefixStyle }],
                null, null, null, heightQ
            )
        } else {
            for (let i = 0; i < line.prefix.length; i++) {
                setCharFn(x++, absY, line.prefix[i], prefixStyle, false, null)
            }
        }
        x = offsetX + line.prefix.length

        // HR-Zeile: Canvas-Strich statt Box-Drawing-Glyphen
        if (line.blockType === 'hr') {
            const charWidth = this.renderer.charWidth
            const charHeight = this.renderer.charHeight
            const pixelY = absY * charHeight
            const hrLen = Math.max(3, Math.floor((this.width - 1) / 3))
            const x1 = x * charWidth
            const x2 = x1 + hrLen * charWidth
            const ctx = this.renderer.ctx
            const style = STYLES[Style.CONSOLE_AI_CODE]
            let color
            if (this.renderer.isNerdMode()) {
                color = this.renderer.colors.foreground[style.intensity]
            } else if (this.renderer.colors.isColored) {
                color = this.renderer.colors.isDarkMode ? style.darkColor : style.lightColor
            } else {
                color = this.renderer.colors.foreground[style.intensity]
            }
            ctx.strokeStyle = color
            ctx.lineWidth = Math.max(1, Math.round(this.renderer.fontSize * 0.06))
            const lineY = pixelY + this.renderer._verticalPad + this.renderer._textHeight * 0.5
            ctx.beginPath()
            ctx.moveTo(x1, lineY)
            ctx.lineTo(x2, lineY)
            ctx.stroke()
            return
        }

        // Code-Bloecke: Mono-Font in der richtigen heightQ-Größe
        if (line.blockType === 'code' || line.forceMonospace) {
            if (this.renderer.isProportional()) {
                // Proportional-Modus: Mono-Glyphen bei heightQ-Größe, gleichmäßiger Abstand
                const charWidth = this.renderer.charWidth
                const charHeight = this.renderer.charHeight
                const textStartX = (offsetX + line.prefix.length) * charWidth
                const pixelY = absY * charHeight
                const lineText = this._getLineText(line)
                const heightQ = line.heightQ || 4
                const formatRuns = [{ start: 0, end: lineText.length, bold: false, italic: false,
                    underline: false, strikethrough: false, style: this._getLineStyle(line) }]

                // Selection-Range: dataOffset ↔ Display-Index ist 1:1 bei Code (kein Marker-Stripping)
                let codeSelRange = null
                const codeSelData = this._getSelectionRangeForLine(line)
                if (codeSelData) {
                    const start = Math.max(0, codeSelData.start - line.startOffset)
                    const end = Math.min(lineText.length, codeSelData.end - line.startOffset)
                    if (end > start) codeSelRange = { start, end }
                }

                this.renderer.renderMonoLineAtHeight(
                    this.renderer.ctx, lineText, textStartX, pixelY, formatRuns, heightQ, codeSelRange
                )
            } else {
                this._renderLine(line, offsetX, absY, setCharFn)
            }
            return
        }

        // Text proportional rendern
        const needsFormatting = line.lineType === 'INPUT' || line.lineType === 'USER' || line.lineType === 'AI'
        let fullText
        if (line.blockText !== null) {
            fullText = line.blockText
        } else if (line.lineType === 'INPUT') {
            fullText = this.model.getInputText()
        } else if (line.entryIndex >= 0) {
            const entry = this.model.getEntries()[line.entryIndex]
            fullText = entry ? entry.text : ''
        }
        if (!fullText && fullText !== '') return

        // Parsed Chars fuer die Zeile
        let parsed
        if (needsFormatting) {
            if (line.lineType === 'INPUT') {
                const formats = this.model.getInputFormats()
                parsed = this._getCachedInputParsed(fullText, formats)
            } else {
                const blockBaseStyle = line.blockText !== null ? this._getBlockBaseStyle(line.blockType) : null
                const cacheKey = line.blockText !== null
                    ? (line.blockCacheKey || `${line.entryIndex}_b${line.blockId}`)
                    : line.entryIndex
                parsed = this._getCachedParsed(cacheKey, fullText, line.lineType, blockBaseStyle)
            }
        } else {
            const style = ENTRY_STYLES[line.lineType] || Style.CONSOLE_SYSTEM
            parsed = []
            for (let i = 0; i < fullText.length; ) {
                const cp = fullText.codePointAt(i)
                const ch = String.fromCodePoint(cp)
                parsed.push({ char: ch, style, dataOffset: i })
                i += ch.length
            }
        }

        // URL-Spans fuer Link-Highlighting
        const textInfo = this._getLineTextInfo(line)
        const urlSpans = textInfo ? this._getUrlSpans(textInfo.fullText, textInfo.cacheKey) : null
        const hasUrls = urlSpans && urlSpans.length > 0

        // Zeilen-Slice (mit URL-Style-Override)
        const lineChars = []
        for (const item of parsed) {
            if (item.dataOffset >= line.endOffset) break
            if (item.dataOffset >= line.startOffset) {
                if (hasUrls && getUrlSpanAtOffset(item.dataOffset, urlSpans)) {
                    lineChars.push({ ...item, style: Style.CONSOLE_LINK })
                } else {
                    lineChars.push(item)
                }
            }
        }

        const lineText = parsedCharsToString(lineChars)
        const formatRuns = enrichFormatRuns(parsedToFormatRuns(lineChars))

        // Selection-Range als Display-Indizes (Input ODER Output)
        let selRange = null
        const selDataRange = this._getSelectionRangeForLine(line)
        if (selDataRange) {
            const { start: selStart, end: selEnd } = selDataRange
            // Data-Offsets in Display-Indizes konvertieren
            let displaySelStart = -1, displaySelEnd = -1
            for (let d = 0; d < lineChars.length; d++) {
                if (lineChars[d].dataOffset >= selStart && displaySelStart < 0) displaySelStart = d
                if (lineChars[d].dataOffset >= selEnd && displaySelEnd < 0) displaySelEnd = d
            }
            if (displaySelStart >= 0 && displaySelEnd < 0) displaySelEnd = lineChars.length
            if (displaySelStart >= 0 && displaySelEnd > displaySelStart) {
                selRange = { start: displaySelStart, end: displaySelEnd }
            }
        }

        // Pixel-Koordinaten
        const charWidth = this.renderer.charWidth
        const charHeight = this.renderer.charHeight
        const textStartX = (offsetX + line.prefix.length) * charWidth
        const pixelY = absY * charHeight
        const paneX = offsetX * charWidth
        const paneWidth = (this.width - 1) * charWidth  // -1 fuer Scrollbar

        // Layout aus PositionMap wiederverwenden
        const posMap = this._getPositionMap(lineIndex)
        const rawLayout = posMap ? posMap._rawLayout : null

        this.renderer.renderProportionalLine(
            this.renderer.ctx,
            lineText,
            textStartX,
            pixelY,
            formatRuns,
            selRange,
            null,  // kein End-Marker in Console
            rawLayout,
            line.heightQ || 4
        )
    }

    /**
     * Berechnet die Selection-Daten-Range fuer eine Zeile (Input ODER Output).
     * Output: vergleicht (entryIndex, blockId) der Zeile gegen Anker/Head der
     * Selection — clampt offset nur fuer die Block-Line, in der Anker bzw.
     * Head sitzt. Bloecke dazwischen werden ganz selektiert.
     * @param {ConsoleLine} line
     * @returns {{start: number, end: number}|null} Daten-Offsets oder null
     */
    _getSelectionRangeForLine(line) {
        // INPUT: bestehende Input-Selection
        if (line.lineType === 'INPUT' && this.model.hasSelection()) {
            const sel = this.model.getInputSelection()
            return {
                start: Math.min(sel.anchor, sel.head),
                end: Math.max(sel.anchor, sel.head)
            }
        }
        // OUTPUT: per Entry-Index + Block-Id pruefen, ob die Zeile zur
        // Output-Selection gehoert.
        if (line.entryIndex >= 0 && this.model.hasOutputSelection()) {
            const sel = this.model.getNormalizedOutputSelection()
            // Collapsed THINKING zeigt nur '[...]' — keine Daten-Highlights.
            const entry = this.model.getEntries()[line.entryIndex]
            if (entry && entry.type === 'THINKING' && entry.collapsed) return null

            // Position der Zeile in (entry, block, offset)-Order: wir vergleichen
            // gegen die Block-Range. Eine Line liegt VOR Anker, wenn ihr Block
            // strikt vor dem Anker-Block liegt, ODER im selben Block aber ihr
            // endOffset <= anchor.offset. Analog fuer "nach Head".
            const linePos = { entryIndex: line.entryIndex, blockId: line.blockId, offset: 0 }
            // Vor-Anker-Check
            if (line.entryIndex < sel.anchor.entryIndex) return null
            if (line.entryIndex === sel.anchor.entryIndex) {
                if (line.blockId < sel.anchor.blockId) return null
                if (line.blockId === sel.anchor.blockId && line.endOffset <= sel.anchor.offset) return null
            }
            // Nach-Head-Check
            if (line.entryIndex > sel.head.entryIndex) return null
            if (line.entryIndex === sel.head.entryIndex) {
                if (line.blockId > sel.head.blockId) return null
                if (line.blockId === sel.head.blockId && line.startOffset >= sel.head.offset) return null
            }

            const inAnchorBlock = (line.entryIndex === sel.anchor.entryIndex && line.blockId === sel.anchor.blockId)
            const inHeadBlock = (line.entryIndex === sel.head.entryIndex && line.blockId === sel.head.blockId)
            const lineStart = inAnchorBlock
                ? Math.max(line.startOffset, sel.anchor.offset)
                : line.startOffset
            const lineEnd = inHeadBlock
                ? Math.min(line.endOffset, sel.head.offset)
                : line.endOffset
            if (lineEnd <= lineStart) return null
            return { start: lineStart, end: lineEnd }
        }
        return null
    }

    /**
     * Gibt den Style fuer einen Zeilen-Typ zurueck.
     * @param {ConsoleLine} line
     * @returns {string} Style-Name
     */
    _getLineStyle(line) {
        if (line.blockType === 'heading') return Style.CONSOLE_AI_HEADING
        if (line.blockType === 'code') return Style.CONSOLE_AI_CODE
        if (line.blockType === 'blockquote') return Style.CONSOLE_AI_BLOCKQUOTE
        if (line.lineType === 'INPUT') return Style.CONSOLE_INPUT
        if (line.isCollapsedPlaceholder) return Style.CONSOLE_THINKING_COLLAPSED
        return ENTRY_STYLES[line.lineType] || Style.CONSOLE_SYSTEM
    }

    /**
     * Gibt den Base-Style fuer einen Block-Typ zurueck (fuer Markdown-Parsing).
     * @param {string} blockType
     * @returns {string|null}
     */
    _getBlockBaseStyle(blockType) {
        if (blockType === 'heading') return Style.CONSOLE_AI_HEADING
        if (blockType === 'blockquote') return Style.CONSOLE_AI_BLOCKQUOTE
        if (blockType === 'code') return Style.CONSOLE_AI_CODE
        return Style.CONSOLE_AI
    }

    // === URL-Erkennung ===

    /**
     * Gibt URL-Spans fuer einen Text zurueck (gecacht).
     * @param {string} text
     * @param {string|number} cacheKey
     * @returns {Array<{start: number, end: number, url: string}>}
     */
    _getUrlSpans(text, cacheKey) {
        const cached = this._urlSpanCache.get(cacheKey)
        if (cached && cached.text === text) return cached.spans
        const spans = findUrlSpans(text)
        this._urlSpanCache.set(cacheKey, { text, spans })
        return spans
    }

    /**
     * Gibt den Display-Text einer Zeile zurueck.
     * @param {ConsoleLine} line
     * @returns {string}
     */
    _getLineText(line) {
        return line.text || ''
    }

    /**
     * Bestimmt den Quelltext und Cache-Key fuer eine Zeile.
     * @param {ConsoleLine} line
     * @returns {{fullText: string, cacheKey: string|number}|null}
     */
    _getLineTextInfo(line) {
        if (line.entryIndex < 0) return null
        if (line.lineType === 'BLANK') return null

        if (line.blockText !== null) {
            return {
                fullText: line.blockText,
                cacheKey: 'url_' + (line.blockCacheKey || `${line.entryIndex}_b${line.blockId}`)
            }
        }
        const entry = this.model.getEntries()[line.entryIndex]
        if (!entry) return null
        return {
            fullText: entry.text,
            cacheKey: 'url_' + line.entryIndex
        }
    }

    /**
     * Gibt die URL an einer Screen-Position zurueck (fuer Klick/Hover).
     * Nutzt pixelY fuer praezise Quarter-Spacing-Aufloesung.
     * @param {number} screenX
     * @param {number} screenY - Character-Y (ganzzahlig)
     * @param {number|null} pixelX
     * @param {number|null} pixelY - Pixel-Y relativ zum Pane (fuer Quarter-Spacing)
     * @returns {string|null} URL oder null
     */
    getLinkAtPosition(screenX, screenY, pixelX = null, pixelY = null) {
        // Zeilen-Index mit Pixel-Praezision bestimmen
        let lineIndex
        if (pixelY !== null && this._useQuarterSpacing() && this.renderer) {
            lineIndex = this._lineAtScreenPixelY(pixelY)
        } else {
            lineIndex = this._lineAtScreenPixelY(screenY * (this.renderer ? this.renderer.charHeight : 1))
        }
        if (lineIndex < 0 || lineIndex >= this.lines.length) return null

        const line = this.lines[lineIndex]
        if (line.entryIndex < 0 || line.lineType === 'BLANK') return null

        const textInfo = this._getLineTextInfo(line)
        if (!textInfo) return null

        const urlSpans = this._getUrlSpans(textInfo.fullText, textInfo.cacheKey)
        if (urlSpans.length === 0) return null

        // Offset innerhalb der Zeile bestimmen (wie screenToEntry)
        let offset
        const posMap = this._getPositionMap(lineIndex)
        if (posMap && pixelX !== null) {
            const textPixelX = pixelX - line.prefix.length * this.renderer.charWidth
            // Zeichen UNTER der Maus (nicht die gerundete Cursor-Grenze) — sonst
            // läge die URL-Trefferzone eine halbe Zeichenbreite links daneben.
            const hitIndex = posMap.getCharIndexUnderX(Math.max(0, textPixelX))
            if (hitIndex < 0) return null
            offset = this._displayIndexToDataOffset(line, hitIndex)
        } else {
            const textX = screenX - line.prefix.length
            if (textX < 0) {
                offset = line.startOffset
            } else {
                offset = line.startOffset + Math.min(textX, line.text.length)
            }
        }

        const span = getUrlSpanAtOffset(offset, urlSpans)
        return span ? span.url : null
    }

    // === Scrollbar ===

    /**
     * Berechnet den Scrollbar-State.
     * @returns {{thumbStart: number, thumbSize: number, totalLines: number, visible: boolean}}
     */
    getScrollbarState() {
        if (this._useQuarterSpacing() && this._totalQuarters > 0) {
            // Quarter-basierte Scrollbar
            const visibleQ = this.height * 4
            if (this._totalQuarters <= visibleQ) {
                return { thumbStart: 0, thumbSize: this.height, totalLines: this.lines.length, visible: false }
            }
            // Mindest-Thumb-Größe (Drag-Handle muss greifbar sein)
            const minThumbChars = this.renderer
                ? Math.ceil(MINIMAP.MIN_VIEWPORT_HEIGHT / this.renderer.charHeight) : 1
            const natural = Math.max(1, Math.round(this.height * visibleQ / this._totalQuarters))
            const thumbSize = Math.min(this.height, Math.max(natural, minThumbChars))
            const vpQ = this._lineQuarterY[this.viewportStart] || 0
            const maxQ = Math.max(1, this._totalQuarters - visibleQ)
            const thumbRange = Math.max(0, this.height - thumbSize)
            const thumbStart = thumbRange > 0 ? Math.round(vpQ * thumbRange / maxQ) : 0
            return { thumbStart, thumbSize, totalLines: this.lines.length, visible: true }
        }

        const total = this.lines.length
        const visible = this.height
        if (total <= visible) {
            return { thumbStart: 0, thumbSize: visible, totalLines: total, visible: false }
        }
        const thumbSize = Math.max(1, Math.round(visible * visible / total))
        const maxStart = Math.max(0, total - visible)
        const thumbStart = maxStart > 0
            ? Math.round(this.viewportStart * (visible - thumbSize) / maxStart)
            : 0
        return { thumbStart, thumbSize, totalLines: total, visible: true }
    }

    /**
     * Rendert die Scrollbar.
     * @param {number} x - X-Position (rechte Spalte)
     * @param {number} y - Y-Start
     * @param {number} height - Hoehe
     * @param {function} setCharFn
     */
    renderScrollbar(x, y, height, setCharFn) {
        const state = this.getScrollbarState()
        const renderer = this.renderer

        // Proportional-Mode: pixelgenaue Minimap mit Entry-Typ-Farben
        if (renderer && this._useQuarterSpacing() && this._totalQuarters > 0) {
            this._renderMinimap(x, y, height, state)
            return
        }

        // Monospace: Thumb als fillRect, Track als ░-Glyph
        const thumbColor = (renderer && state.visible)
            ? renderer._resolveStyle(Style.SCROLLBAR_THUMB).color : null

        for (let i = 0; i < height; i++) {
            const isThumb = state.visible && i >= state.thumbStart && i < state.thumbStart + state.thumbSize
            if (isThumb && thumbColor) {
                const cw = renderer.charWidth
                const ch = renderer.charHeight
                renderer.ctx.fillStyle = thumbColor
                renderer.ctx.fillRect(x * cw, (y + i) * ch, cw, ch)
            } else if (isThumb) {
                // Fallback (Tests ohne Renderer)
                setCharFn(x, y + i, '█', Style.SCROLLBAR_THUMB, false, null)
            } else {
                setCharFn(x, y + i, '░', Style.SCROLLBAR_TRACK, false, null)
            }
        }
    }

    /**
     * Rendert die Scrollbar im Proportional-Mode als pixelgenaue Minimap:
     * Viewport-Underlayer + farbige Blöcke pro ConsoleEntry-Typ
     * (USER/AI/SYSTEM/ERROR/THINKING).
     *
     * @param {number} x - Char-Spalte
     * @param {number} y - Char-Y-Start
     * @param {number} height - Höhe in Chars
     * @param {{visible: boolean}} state - Scrollbar-State
     */
    _renderMinimap(x, y, height, state) {
        const renderer = this.renderer
        const ctx = renderer.ctx
        const cw = renderer.charWidth
        const ch = renderer.charHeight

        const px = x * cw
        const py = y * ch
        const pw = cw
        const ph = height * ch

        // Opaker Hintergrund — keine Shift-Reste unter den Alpha-Layern
        ctx.fillStyle = renderer.colors.background
        ctx.fillRect(px, py, pw, ph)

        const quarterH = ch / 4
        const totalQ = Math.max(1, this._totalQuarters)
        const qToPixel = (q) => py + Math.max(0, Math.min(1, q / totalQ)) * ph

        // Basis-Ton für den Viewport-Layer (neutraler AI-Ton, passt zum Konsolen-Look)
        const fgColor = renderer._resolveStyle(Style.CONSOLE_AI).color

        // Minimap-Inhalt etwas schmaler als die Spalte — der Viewport (volle Breite)
        // ragt beidseitig um `inset` Pixel heraus und wird dadurch deutlich sichtbar.
        const inset = Math.min(MINIMAP.INSET, Math.floor((pw - 1) / 2))
        const mpx = px + inset
        const mpw = Math.max(1, pw - 2 * inset)

        // --- Viewport-Underlayer ---
        // vpBounds merken — Rand-Streifen bekommen am Ende einen zweiten Pass mit
        // höherem Alpha, damit der Viewport-Überstand sichtbar ist.
        let vpBounds = null
        if (state.visible) {
            const vpQStart = (this._lineQuarterY[this.viewportStart] || 0) + (this._viewportPixelOffset || 0) / quarterH
            const vpQEnd = vpQStart + height * 4
            let vpPxStart = qToPixel(vpQStart)
            let vpPxEnd = qToPixel(Math.min(vpQEnd, totalQ))

            const minH = MINIMAP.MIN_VIEWPORT_HEIGHT
            if (vpPxEnd - vpPxStart < minH) {
                const mid = (vpPxStart + vpPxEnd) / 2
                vpPxStart = mid - minH / 2
                vpPxEnd = mid + minH / 2
                if (vpPxStart < py) { vpPxEnd += py - vpPxStart; vpPxStart = py }
                if (vpPxEnd > py + ph) { vpPxStart -= vpPxEnd - (py + ph); vpPxEnd = py + ph }
            }

            const vpY = Math.round(vpPxStart)
            const vpH = Math.round(vpPxEnd - vpPxStart)

            ctx.save()
            ctx.globalAlpha = MINIMAP.VIEWPORT_ALPHA
            ctx.fillStyle = fgColor
            ctx.fillRect(px, vpY, pw, vpH)
            ctx.restore()

            vpBounds = { y: vpY, h: vpH }
        }

        // --- Entry-Typ-Farben auflösen (pro Render einmal) ---
        const entryStyles = {
            'USER':         { color: renderer._resolveStyle(Style.CONSOLE_USER).color,         alpha: MINIMAP.ENTRY_USER_ALPHA },
            'AI':           { color: renderer._resolveStyle(Style.CONSOLE_AI).color,           alpha: MINIMAP.ENTRY_AI_ALPHA },
            'SYSTEM':       { color: renderer._resolveStyle(Style.CONSOLE_SYSTEM).color,       alpha: MINIMAP.ENTRY_SYSTEM_ALPHA },
            'ERROR':        { color: renderer._resolveStyle(Style.CONSOLE_ERROR).color,        alpha: MINIMAP.ENTRY_ERROR_ALPHA },
            'THINKING':     { color: renderer._resolveStyle(Style.CONSOLE_AI).color,           alpha: MINIMAP.ENTRY_AI_ALPHA * 0.5 },
            'REVIEW_PANEL': { color: renderer._resolveStyle(Style.CONSOLE_REVIEW_PANEL).color, alpha: MINIMAP.ENTRY_AI_ALPHA }
        }

        // --- Farb-Blöcke pro Line-Run zusammenfassen (kein Mikro-Gap zwischen gleichen Typen) ---
        ctx.save()
        let runType = null
        let runStartQ = 0
        for (let i = 0; i <= this.lines.length; i++) {
            const line = i < this.lines.length ? this.lines[i] : null
            const type = (line && entryStyles[line.lineType]) ? line.lineType : null

            if (type !== runType) {
                // Vorigen Run abschließen
                if (runType !== null) {
                    const runEndQ = this._lineQuarterY[i] || (runStartQ)
                    const pyStart = Math.round(qToPixel(runStartQ))
                    const pyEnd = Math.round(qToPixel(runEndQ))
                    if (pyEnd > pyStart) {
                        const st = entryStyles[runType]
                        ctx.globalAlpha = st.alpha
                        ctx.fillStyle = st.color
                        ctx.fillRect(mpx, pyStart, mpw, pyEnd - pyStart)
                    }
                }
                // Neuer Run startet bei dieser Line (oder bleibt "kein Run")
                runType = type
                runStartQ = type !== null ? (this._lineQuarterY[i] || 0) : 0
            }
        }
        ctx.restore()

        // --- Viewport-Rand-Streifen (Überstand) mit stärkerem Alpha nachzeichnen ---
        if (vpBounds && inset > 0) {
            ctx.save()
            ctx.globalAlpha = MINIMAP.VIEWPORT_EDGE_ALPHA
            ctx.fillStyle = fgColor
            ctx.fillRect(px, vpBounds.y, inset, vpBounds.h)
            ctx.fillRect(px + pw - inset, vpBounds.y, inset, vpBounds.h)
            ctx.restore()
        }

        // Input-Cursor wird in der Core-Minimap nicht angezeigt — es gibt nur eine
        // logische Cursor-Position (in der Input-Zeile am Ende), das ist redundant.
    }

    // === Scrollbar-Interaktion ===

    /**
     * Prüft ob eine X-Position auf der Scrollbar-Spalte liegt.
     * @param {number} x - X relativ zum Pane (0 .. width-1)
     * @returns {boolean}
     */
    isOnScrollbar(x) {
        return x === this.width - 1
    }

    /**
     * Prüft ob eine Y-Position auf dem Viewport-Handle liegt.
     * @param {number} y - Y in Chars
     * @returns {boolean}
     */
    isOnScrollbarThumb(y) {
        const state = this.getScrollbarState()
        if (!state.visible) return false
        return y >= state.thumbStart && y < state.thumbStart + state.thumbSize
    }

    /**
     * Scrollt zu einer Klick-Position auf der Scrollbar. Zentriert den Ziel-Punkt
     * in der Viewport-Mitte (wie im Editor).
     * @param {number} y - Y in Chars (fractional möglich)
     */
    scrollToScrollbarY(y) {
        const h = this.height
        if (this._useQuarterSpacing() && this._totalQuarters > 0 && this.renderer) {
            const quarterH = this.renderer.charHeight / 4
            const visibleQ = h * 4
            const totalQ = this._totalQuarters
            const scrollRangeQ = Math.max(0, totalQ - visibleQ)

            const ratio = Math.max(0, Math.min(1, y / h))
            const targetQ = ratio * totalQ
            const newStartQ = Math.max(0, Math.min(scrollRangeQ, targetQ - visibleQ / 2))

            let lo = 0, hi = this._lineQuarterY.length - 1
            while (lo < hi) {
                const mid = (lo + hi + 1) >> 1
                if (this._lineQuarterY[mid] <= newStartQ) lo = mid
                else hi = mid - 1
            }
            this.viewportStart = lo
            this._viewportPixelOffset = (this._lineQuarterY[lo] < newStartQ)
                ? (newStartQ - this._lineQuarterY[lo]) * quarterH
                : 0
            this.isDirty = true
            return
        }

        // Monospace
        const total = this.lines.length
        if (total <= h) return
        const ratio = y / h
        const targetLine = Math.floor(ratio * total)
        const maxStart = Math.max(0, total - h)
        this.viewportStart = Math.max(0, Math.min(maxStart, targetLine - Math.floor(h / 2)))
        this.isDirty = true
    }

    /**
     * Scrollt per Drag auf dem Viewport-Handle.
     * @param {number} deltaY - Delta seit Drag-Start in Chars (fractional möglich)
     * @param {number} startViewport - Viewport-Start bei Drag-Beginn
     */
    scrollByThumbDrag(deltaY, startViewport) {
        const h = this.height
        if (this._useQuarterSpacing() && this._totalQuarters > 0 && this.renderer) {
            const state = this.getScrollbarState()
            const thumbRange = h - state.thumbSize
            if (thumbRange <= 0) return

            const visibleQ = h * 4
            const scrollRangeQ = Math.max(0, this._totalQuarters - visibleQ)
            const qPerChar = scrollRangeQ / thumbRange
            const startQ = this._lineQuarterY[startViewport] || 0
            const targetQ = Math.max(0, Math.min(scrollRangeQ, startQ + deltaY * qPerChar))

            let lo = 0, hi = this._lineQuarterY.length - 1
            while (lo < hi) {
                const mid = (lo + hi + 1) >> 1
                if (this._lineQuarterY[mid] <= targetQ) lo = mid
                else hi = mid - 1
            }
            const quarterH = this.renderer.charHeight / 4
            this.viewportStart = lo
            this._viewportPixelOffset = (this._lineQuarterY[lo] < targetQ)
                ? (targetQ - this._lineQuarterY[lo]) * quarterH
                : 0
            this.isDirty = true
        } else {
            const total = this.lines.length
            const maxStart = Math.max(0, total - h)
            if (maxStart <= 0) return
            const thumbRange = h - Math.max(1, Math.round(h * h / total))
            if (thumbRange <= 0) return
            const linesPerChar = maxStart / thumbRange
            const deltaLines = Math.round(deltaY * linesPerChar)
            this.viewportStart = Math.max(0, Math.min(maxStart, startViewport + deltaLines))
            this.isDirty = true
        }
    }

    // === Bounds ===

    /**
     * Aktualisiert die Pane-Groesse.
     * @param {{width: number, height: number}} bounds
     */
    updateBounds(bounds) {
        const changed = this.width !== bounds.width || this.height !== bounds.height
        this.width = bounds.width
        this.height = bounds.height
        if (changed) {
            this._viewportPixelOffset = 0
            this.rebuildLines()
            this.scrollToBottom()
        }
    }

    // === Formatting ===

    /**
     * Gibt gecachte parsed-Markdown-Daten zurueck (text-basierte Invalidierung).
     * @param {number} cacheKey - Cache-Schluessel (-1 fuer Input, sonst entryIndex)
     * @param {string} fullText
     * @param {string} lineType
     * @returns {Array}
     */
    _getCachedParsed(cacheKey, fullText, lineType, blockBaseStyle = null) {
        const cached = this._parsedCache.get(cacheKey)
        if (cached && cached.text === fullText) return cached.parsed
        const baseStyle = blockBaseStyle || this._lineTypeToBaseStyle(lineType)
        const parsed = parseToStyledChars(fullText, baseStyle, { skipNewlines: true })
        this._parsedCache.set(cacheKey, { text: fullText, parsed })
        // Cache-Groesse begrenzen: aelteste Eintraege entfernen
        if (this._parsedCache.size > 500) {
            const firstKey = this._parsedCache.keys().next().value
            this._parsedCache.delete(firstKey)
        }
        return parsed
    }

    /**
     * Konvertiert einen lineType in den passenden Base-Style.
     * @param {string} lineType
     * @returns {string}
     */
    _lineTypeToBaseStyle(lineType) {
        if (lineType === 'INPUT') return Style.CONSOLE_INPUT
        if (lineType === 'AI') return Style.CONSOLE_AI
        return Style.CONSOLE_USER
    }

    /**
     * Gibt die Format-Spans fuer den Input-Text zurueck.
     * Im RT-Modus direkt aus dem Model.
     * @returns {Array<{start: number, end: number, type: string}>}
     */
    getInputFormatSpans() {
        return this.model.getInputFormats()
    }

    /**
     * Gibt gecachte StyledChars fuer den Input zurueck (RT-basiert).
     * @param {string} text
     * @param {Array} formats
     * @returns {Array}
     */
    _getCachedInputParsed(text, formats) {
        // Cache-Key basiert auf Text + Formats-Referenz
        const cacheKey = -1
        const cached = this._parsedCache.get(cacheKey)
        if (cached && cached.text === text && cached.formats === formats) {
            return cached.parsed
        }
        const parsed = buildStyledChars(text, formats, [], Style.CONSOLE_INPUT, null)
        this._parsedCache.set(cacheKey, { text, parsed, formats })
        return parsed
    }
}
